import PreviewLayout from "./preview-layout";
import type { FlowSection, Question } from "../form-builder/types";
import type { BrandKit } from "@/modules/brand";
import { getTextColorForBackground } from "@/lib/utils";

interface PreviewWelcomeProps {
	id: string;
	section: FlowSection;
	brandName: string;
	brandLogo: string | null;
	brandkit: BrandKit;
	isSelected: boolean;
	onTitleChange: (title: string) => void;
	onDescriptionChange: (description: string) => void;
	questionCount?: number;
	questionFormats?: Question["format"][];
	companyDetailEnabled?: boolean;
	isDarkMode?: boolean;
}

// Generate format description text based on available formats
function getFormatDescription(formats: Question["format"][]): string {
	// Collect unique formats, treating "any" as all formats
	const hasAny = formats.includes("any");
	const uniqueFormats = new Set<string>();

	formats.forEach((format) => {
		if (format === "any") {
			uniqueFormats.add("text");
			uniqueFormats.add("audio");
			uniqueFormats.add("video");
		} else {
			uniqueFormats.add(format);
		}
	});

	const formatList = Array.from(uniqueFormats);

	// If all three formats or has "any", show choice text
	if (formatList.length === 3 || hasAny) {
		return "Text, audio or video, your choice";
	}

	// Single format
	if (formatList.length === 1) {
		const format = formatList[0];
		if (format === "text") return "Share your thoughts in writing";
		if (format === "audio") return "Record your voice";
		if (format === "video") return "Record a video";
	}

	// Two formats - create "X or Y" text
	if (formatList.length === 2) {
		const capitalizedFormats = formatList.map((f) => f.charAt(0).toUpperCase() + f.slice(1));
		return `${capitalizedFormats.join(" or ")}, your choice`;
	}

	return "Text, audio or video, your choice";
}

export default function PreviewWelcome({
	id,
	section,
	brandName,
	brandLogo,
	brandkit,
	isSelected,
	onTitleChange,
	onDescriptionChange,
	questionCount = 1,
	questionFormats = [],
	companyDetailEnabled = false,
	isDarkMode = false,
}: PreviewWelcomeProps) {
	void isSelected;

	const formatDescription = getFormatDescription(questionFormats);

	return (
		<PreviewLayout
			id={id}
			brandName={brandName}
			brandLogo={brandLogo}
			brandkit={brandkit}
			title={section.title}
			titlePlaceholder="Welcome!"
			subtitle={section.description}
			subtitlePlaceholder="Add a welcome message..."
			showBackButton
			editableTitle
			editableSubtitle
			onLocalTitleChange={onTitleChange}
			onLocalSubtitleChange={onDescriptionChange}
			isDarkMode={isDarkMode}
		>
			<div className="space-y-3 text-left">
				{(() => {
					let stepNumber = 1;
					return (
						<>
							<div className="flex items-center gap-4 py-4 rounded-xl">
								<div
									className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
									style={{ backgroundColor: "var(--color-accent, #e17100)", color: getTextColorForBackground(brandkit.color_accent_hex) }}
								>
									{stepNumber++}
								</div>
								<div>
									<p className={`font-medium ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
										Answer {questionCount} quick {questionCount === 1 ? "question" : "questions"}
									</p>
									<p className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
										{formatDescription}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-4 py-4 rounded-xl">
								<div
									className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
									style={{ backgroundColor: "var(--color-accent, #e17100)", color: getTextColorForBackground(brandkit.color_accent_hex) }}
								>
									{stepNumber++}
								</div>
								<div>
									<p className={`font-medium ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
										Add your details
									</p>
									<p className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
										Name, email, and optionally your photo
									</p>
								</div>
							</div>
							{companyDetailEnabled && (
								<div className="flex items-center gap-4 py-4 rounded-xl">
									<div
										className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
										style={{ backgroundColor: "var(--color-accent, #e17100)", color: getTextColorForBackground(brandkit.color_accent_hex) }}
									>
										{stepNumber++}
									</div>
									<div>
										<p className={`font-medium ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
											Add company details
										</p>
										<p className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
											Job title, company, website and logo
										</p>
									</div>
								</div>
							)}
							<div className="flex items-center gap-4 py-4 rounded-xl">
								<div
									className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
									style={{ backgroundColor: "var(--color-accent, #e17100)", color: getTextColorForBackground(brandkit.color_accent_hex) }}
								>
									{stepNumber++}
								</div>
								<div>
									<p className={`font-medium ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
										Choose your privacy
									</p>
									<p className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
										You control where it's shared
									</p>
								</div>
							</div>
						</>
					);
				})()}
			</div>
		</PreviewLayout>
	);
}
